import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2, LogOut, RefreshCw, X, Sliders, BarChart3, Eye, Globe, TrendingUp, Sparkles, AlertTriangle } from "lucide-react";
import Header from "../components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  incomplete: "bg-[#FFF7ED] text-[#475569]",
  pending_review: "bg-[#C78229]/15 text-[#C78229]",
  ready_for_release: "bg-[#E78938]/15 text-[#4A6B53]",
};

export default function AdminDashboard() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [token] = useState(localStorage.getItem("dz_admin_token"));
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wipeBusy, setWipeBusy] = useState(false);

  useEffect(() => {
    if (!token) { nav("/admin"); return; }
    refresh();
  }, [token, filter]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/analytics?days=${analyticsDays}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAnalytics(r.data))
      .catch(() => {});
  }, [token, analyticsDays]);

  const refresh = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = filter !== "all" ? { status: filter } : {};
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers, params }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(l.data.leads);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("dz_admin_token"); nav("/admin"); }
      else toast.error(t("admin.dashboard.toasts.load_error"));
    } finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t("admin.dashboard.toasts.status_updated"));
      refresh();
      if (selected) {
        const refreshed = await axios.get(`${API}/admin/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setSelected(refreshed.data);
      }
    } catch { toast.error(t("admin.dashboard.toasts.status_error")); }
  };

  const openLead = async (id) => {
    try {
      const r = await axios.get(`${API}/admin/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(r.data);
    } catch { toast.error(t("admin.dashboard.toasts.lead_load_error")); }
  };

  const logout = () => { localStorage.removeItem("dz_admin_token"); nav("/admin"); };

  const runWipe = async () => {
    if (wipeConfirm !== "WIPE_DOCTORSOL_DATA") {
      toast.error("Bevestigingstekst klopt niet");
      return;
    }
    setWipeBusy(true);
    try {
      const r = await axios.post(`${API}/admin/wipe-data`,
        { confirm: wipeConfirm },
        { headers: { Authorization: `Bearer ${token}` } });
      const counts = Object.entries(r.data.deleted || {})
        .map(([k, v]) => `${k}: ${v}`).join(" · ");
      toast.success(`Data gewist — ${counts}`);
      setWipeOpen(false);
      setWipeConfirm("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wissen mislukt");
    } finally { setWipeBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <Header minimal />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#E78938] mb-1">{t("admin.dashboard.kicker")}</div>
            <h1 className="serif text-4xl text-[#0F172A]">{t("admin.dashboard.title")}</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/market" data-testid="nav-market"
              className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-2.5 hover:bg-[#FFF7ED] flex items-center gap-2 text-sm text-[#E78938]">
              <TrendingUp className="w-4 h-4" /> {t("admin.dashboard.market_button", "Market Trends")}
            </Link>
            <Link to="/admin/pricing" data-testid="nav-pricing"
              className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-2.5 hover:bg-[#FFF7ED] flex items-center gap-2 text-sm text-[#E78938]">
              <Sliders className="w-4 h-4" /> {t("admin.dashboard.pricing_button")}
            </Link>
            <button onClick={refresh} data-testid="refresh-btn" className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-2.5 hover:bg-[#FFF7ED] flex items-center gap-2 text-sm">
              <RefreshCw className="w-4 h-4" /> {t("admin.dashboard.refresh")}
            </button>
            <button onClick={logout} data-testid="logout-btn" className="bg-[#0F172A] text-white rounded-xl px-4 py-2.5 hover:bg-[#1E293B] flex items-center gap-2 text-sm">
              <LogOut className="w-4 h-4" /> {t("admin.dashboard.logout")}
            </button>
            <button onClick={() => setWipeOpen(true)} data-testid="open-wipe-btn"
              title="Wis alle leads / payments / market data"
              className="bg-white border border-[#FECACA] text-[#B91C1C] rounded-xl px-3 py-2.5 hover:bg-[#FEF2F2] flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-row">
            <Tile k={t("admin.dashboard.leads")} v={stats.total} />
            <Tile k={t("admin.dashboard.paid")} v={stats.paid} />
            <Tile k={t("admin.dashboard.revenue")} v={`€ ${stats.revenue_eur.toLocaleString()}`} accent />
            <Tile k={t("admin.dashboard.active_regions")} v={stats.by_region.length} />
          </div>
        )}

        {/* Analytics widget */}
        {analytics && (
          <div className="bg-white rounded-2xl border border-[#FFF7ED] p-7 mb-8" data-testid="analytics-widget">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#E78938]" />
                <h2 className="serif text-2xl text-[#0F172A]">{t("admin.dashboard.analytics.title")}</h2>
              </div>
              <div className="flex gap-2">
                {[1, 7, 30, 90].map((d) => (
                  <button key={d} onClick={() => setAnalyticsDays(d)}
                    data-testid={`analytics-${d}d`}
                    className={`px-3 py-1.5 text-xs rounded-lg ${analyticsDays === d
                      ? "bg-[#0F172A] text-white" : "bg-[#FFF7ED] text-[#475569]"}`}>
                    {d === 1 ? t("admin.dashboard.analytics.today") : `${d}d`}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <MiniTile icon={<Eye className="w-4 h-4" />} k={t("admin.dashboard.analytics.pageviews")} v={analytics.total_pageviews.toLocaleString()} />
              <MiniTile icon={<Globe className="w-4 h-4" />} k={t("admin.dashboard.analytics.uniques")} v={analytics.total_uniques.toLocaleString()} accent />
              <MiniTile icon={<TrendingUp className="w-4 h-4" />} k={t("admin.dashboard.analytics.leads")} v={analytics.leads_in_window} />
              <MiniTile k={t("admin.dashboard.analytics.conv")} v={`${(analytics.conversion_rate * 100).toFixed(1)}%`} />
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#E78938] mb-2 font-semibold">{t("admin.dashboard.analytics.top_pages")}</div>
                <div className="space-y-1">
                  {(analytics.top_pages || []).slice(0, 5).map((p) => (
                    <div key={p.path} className="flex justify-between text-sm gap-2">
                      <span className="truncate text-[#0F172A]">{p.path}</span>
                      <span className="text-[#94A3B8] tnum shrink-0">{p.views}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#E78938] mb-2 font-semibold">{t("admin.dashboard.analytics.top_referrers")}</div>
                <div className="space-y-1">
                  {(analytics.top_referrers || []).length === 0
                    ? <div className="text-xs text-[#94A3B8]">{t("admin.dashboard.analytics.no_referrer")}</div>
                    : (analytics.top_referrers || []).slice(0, 5).map((p, i) => {
                        let host = p.ref;
                        try { host = new URL(p.ref).hostname; } catch {}
                        return (
                          <div key={i} className="flex justify-between text-sm gap-2">
                            <span className="truncate text-[#0F172A]">{host}</span>
                            <span className="text-[#94A3B8] tnum shrink-0">{p.views}</span>
                          </div>
                        );
                      })}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#E78938] mb-2 font-semibold">{t("admin.dashboard.analytics.languages")}</div>
                <div className="space-y-1">
                  {(analytics.by_lang || []).map((p) => (
                    <div key={p.lang} className="flex justify-between text-sm gap-2">
                      <span className="text-[#0F172A] uppercase">{p.lang}</span>
                      <span className="text-[#94A3B8] tnum shrink-0">{p.views}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {analytics.daily?.length > 0 && (
              <div className="mt-6 pt-5 border-t border-[#FFF7ED]">
                <div className="text-[10px] uppercase tracking-wider text-[#E78938] mb-3 font-semibold">{t("admin.dashboard.analytics.per_day")}</div>
                <div className="flex items-end gap-1 h-24">
                  {analytics.daily.map((d) => {
                    const max = Math.max(...analytics.daily.map((x) => x.uniques), 1);
                    const h = Math.max(2, (d.uniques / max) * 100);
                    return (
                      <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.uniques}`}>
                        <div className="w-full bg-[#E78938]/30 rounded-t" style={{ height: `${h}%` }} />
                        <div className="text-[9px] text-[#94A3B8] mono whitespace-nowrap">{d.day.slice(5)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-5 flex-wrap" data-testid="filters">
          {["all", "incomplete", "pending_review", "ready_for_release"].map((k) => (
            <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
              className={`px-4 py-2 rounded-full text-sm transition ${filter === k ? "bg-[#0F172A] text-white" : "bg-white border border-[#FFF7ED] text-[#475569]"}`}>
              {t(`admin.dashboard.filters.${k}`)}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-[#FFF7ED] overflow-hidden table-scroll">
          {loading ? (
            <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#E78938]" /></div>
          ) : leads.length === 0 ? (
            <div className="p-10 text-center text-[#475569]">{t("admin.dashboard.table.empty")}</div>
          ) : (
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-[#FFF7ED]/40 text-[#E78938] uppercase text-xs tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">{t("admin.dashboard.table.client")}</th>
                  <th className="text-left px-5 py-3">{t("admin.dashboard.table.region")}</th>
                  <th className="text-left px-5 py-3">{t("admin.dashboard.table.bill")}</th>
                  <th className="text-left px-5 py-3">{t("admin.dashboard.table.status")}</th>
                  <th className="text-left px-5 py-3">{t("admin.dashboard.table.payment")}</th>
                  <th className="text-right px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-[#FFF7ED] hover:bg-[#FFFFFF]/50">
                    <td className="px-5 py-4">
                      <div className="text-[#0F172A] font-medium">{l.name}</div>
                      <div className="text-[#475569] text-xs">{l.email}</div>
                    </td>
                    <td className="px-5 py-4 capitalize">{l.region}</td>
                    <td className="px-5 py-4 tnum">€ {l.monthly_bill_eur}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs ${STATUS_STYLES[l.dossier_status]}`}>
                        {l.dossier_status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-5 py-4">{l.paid ? <span className="text-[#4A6B53]">{t("admin.dashboard.table.paid_yes")}</span> : <span className="text-[#475569]">—</span>}</td>
                    <td className="px-5 py-4 text-right">
                      <button data-testid={`open-${l.id}`} onClick={() => openLead(l.id)} className="text-[#E78938] hover:underline">{t("admin.dashboard.table.open")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-[#0F172A]/40 backdrop-blur-sm z-50 flex justify-end" onClick={() => setSelected(null)} data-testid="lead-drawer">
          <div className="w-full max-w-xl bg-[#FFFFFF] h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-7 border-b border-[#FFF7ED] flex justify-between items-start sticky top-0 bg-[#FFFFFF]">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#E78938] mb-1">{t("admin.dashboard.drawer.label")}</div>
                <h2 className="serif text-2xl text-[#0F172A]">{selected.name}</h2>
                <div className="text-sm text-[#475569]">{selected.email} · {selected.whatsapp}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#475569]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-7 space-y-6">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <KV k={t("admin.dashboard.drawer.region")} v={selected.region} />
                <KV k={t("admin.dashboard.drawer.bill")} v={`€ ${selected.monthly_bill_eur}`} />
                <KV k={t("admin.dashboard.drawer.paid")} v={selected.paid ? t("admin.dashboard.drawer.yes") : t("admin.dashboard.drawer.no")} />
                <KV k={t("admin.dashboard.drawer.status")} v={selected.dossier_status} />
              </div>

              {(selected.referrer || (selected.referred_count || 0) > 0) && (
                <div data-testid="referral-info" className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-4 flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-[#E78938] mt-0.5 shrink-0" />
                  <div className="text-sm space-y-1">
                    {selected.referrer && (
                      <div>
                        <span className="text-[#475569]">{t("admin.dashboard.drawer.referred_by")}: </span>
                        <span className="text-[#0F172A] font-medium">{selected.referrer.name}</span>
                        <span className="text-[#94A3B8]"> ({selected.referrer.email}) · </span>
                        <span className="font-mono text-[#E78938]">{selected.referrer.referral_code}</span>
                      </div>
                    )}
                    {(selected.referred_count || 0) > 0 && (
                      <div>
                        <span className="text-[#475569]">{t("admin.dashboard.drawer.referred_count")}: </span>
                        <span className="text-[#0F172A] font-medium">{selected.referred_count}</span>
                        <span className="text-[#94A3B8]"> · {selected.referred_paid_count} {t("admin.dashboard.drawer.referred_paid")}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selected.summer_sale_free && (
                <div data-testid="summer-sale-info" className="bg-gradient-to-br from-[#E78938]/10 to-[#FFF7ED] border border-[#E78938]/20 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#E78938] mb-2 font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#E78938] rounded-full" /> Summer Sale · slot #{selected.summer_sale_slot}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-[#94A3B8]">PDF gegenereerd</div>
                      <div className="font-medium text-[#0F172A]">{selected.summer_sale_pdf_scheduled ? "Ja" : "Nog niet"}</div>
                    </div>
                    <div>
                      <div className="text-[#94A3B8]">Verzending</div>
                      <div className="font-medium text-[#0F172A]">
                        {selected.summer_sale_pdf_sent
                          ? `✓ Verzonden ${(selected.summer_sale_pdf_sent_at || "").slice(0, 16).replace("T", " ")}`
                          : selected.summer_sale_pdf_target
                            ? `Gepland ${selected.summer_sale_pdf_target.slice(0, 16).replace("T", " ")}`
                            : "—"}
                      </div>
                    </div>
                  </div>
                  {selected.summer_sale_pdf_scheduled && (
                    <a href={`${API}/report/${selected.id}/pdf`} target="_blank" rel="noreferrer"
                      data-testid="dossier-download-link"
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#E78938] hover:underline font-semibold">
                      Download rapport →
                    </a>
                  )}
                </div>
              )}

              {selected.utm && Object.keys(selected.utm).length > 0 && (
                <div data-testid="utm-info" className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#E78938] mb-2 font-semibold">UTM / Campaign</div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    {Object.entries(selected.utm).map(([k, v]) => (
                      <div key={k} className="flex gap-1.5">
                        <span className="text-[#94A3B8] shrink-0">{k}:</span>
                        <span className="text-[#0F172A] truncate font-mono" title={String(v)}>{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="serif text-xl mb-3">{t("admin.dashboard.drawer.change_status")}</h3>
                <div className="flex gap-2 flex-wrap">
                  {["incomplete", "pending_review", "ready_for_release"].map((s) => (
                    <button key={s} data-testid={`set-${s}`} onClick={() => updateStatus(selected.id, s)}
                      className={`px-3 py-2 rounded-lg text-sm border ${selected.dossier_status === s ? "bg-[#E78938] text-white border-[#E78938]" : "bg-white border-[#FFF7ED] text-[#0F172A]"}`}>
                      {t(`admin.dashboard.filters.${s}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="serif text-xl mb-3">{t("admin.dashboard.drawer.intake_data")}</h3>
                <pre className="bg-white border border-[#FFF7ED] rounded-xl p-4 text-xs overflow-x-auto">
                  {JSON.stringify({ intake: selected.intake, roi: selected.roi }, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Danger zone — wipe operational data */}
      {wipeOpen && (
        <div className="fixed inset-0 bg-[#0F172A]/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
             onClick={() => !wipeBusy && setWipeOpen(false)} data-testid="wipe-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-7 border border-[#FECACA]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-[#FEF2F2] rounded-lg shrink-0">
                <AlertTriangle className="w-5 h-5 text-[#B91C1C]" />
              </div>
              <div>
                <h3 className="serif text-xl text-[#0F172A] mb-1">Productiedata wissen</h3>
                <p className="text-sm text-[#475569]">
                  Verwijdert <strong>leads, payment_transactions, quote_market_data, visits, promo_redemptions</strong>.
                  Behouden: admins, promo_codes, pricing, financing, installers. Onomkeerbaar.
                </p>
              </div>
            </div>
            <label className="block text-xs uppercase tracking-wider text-[#475569] font-semibold mb-1.5">
              Typ ter bevestiging: <span className="font-mono text-[#B91C1C]">WIPE_DOCTORSOL_DATA</span>
            </label>
            <input
              value={wipeConfirm}
              onChange={(e) => setWipeConfirm(e.target.value)}
              data-testid="wipe-confirm-input"
              autoFocus
              className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm font-mono mb-4 focus:outline-none focus:border-[#B91C1C]"
              placeholder="WIPE_DOCTORSOL_DATA"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setWipeOpen(false); setWipeConfirm(""); }}
                disabled={wipeBusy}
                className="px-4 py-2 rounded-lg text-sm border border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]">
                Annuleren
              </button>
              <button onClick={runWipe} disabled={wipeBusy || wipeConfirm !== "WIPE_DOCTORSOL_DATA"}
                data-testid="wipe-confirm-btn"
                className="px-4 py-2 rounded-lg text-sm bg-[#B91C1C] text-white hover:bg-[#991B1B] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                {wipeBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                Wis data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Tile = ({ k, v, accent }) => (
  <div className={`rounded-xl p-5 border ${accent ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#FFF7ED]"}`}>
    <div className={`text-xs uppercase tracking-wider ${accent ? "text-[#FED7AA]" : "text-[#E78938]"}`}>{k}</div>
    <div className={`serif text-3xl tnum mt-1 ${accent ? "text-[#E78938]" : "text-[#0F172A]"}`}>{v}</div>
  </div>
);
const MiniTile = ({ icon, k, v, accent }) => (
  <div className={`rounded-xl p-4 border ${accent ? "bg-[#FFF7ED] border-[#FED7AA]" : "bg-[#F8FAFC] border-[#E2E8F0]"}`}>
    <div className="flex items-center gap-1.5 text-[#E78938] uppercase text-[10px] tracking-wider font-semibold">
      {icon}<span>{k}</span>
    </div>
    <div className={`serif text-2xl tnum mt-0.5 ${accent ? "text-[#E78938]" : "text-[#0F172A]"}`}>{v}</div>
  </div>
);
const KV = ({ k, v }) => (
  <div className="bg-white border border-[#FFF7ED] rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider text-[#E78938]">{k}</div>
    <div className="text-[#0F172A] mt-0.5">{v}</div>
  </div>
);
